import { useMutation } from "@apollo/client";
import { getMentorshipGroupAddOnPrice } from "@app/shared/prices";
import {
    Membership,
    MembershipBillingChoice,
    MembershipBillingPlan,
    MembershipType,
    ProductFeature,
} from "@app/shared/types";
import { List, ListItemButton, ListItemText, Typography } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Box } from "@mui/system";
import useLocalStorage from "@rehooks/local-storage";
import { GRAPHQL_MUTATION_MEMBERSHIP_BILLING } from "app/queries";
import { routes } from "app/routes";
import { showSnackbarAlertOnRedirect } from "components/AlertSnackBar";
import { displayPrice } from "components/displayPrice";
import { GenericErrorPage } from "components/GenericErrorPage";
import { push } from "connected-react-router";
import { LoggedInUserProfileContext } from "features/auth/RequireAuthentication";
import { LandingTwoColumnLayout } from "features/landingPage/FunnelMVP/LandingTwoColumnLayout";
import { LoadingPage } from "features/pages/LoadingPage";
import NotFoundPage from "features/pages/NotFoundPage";
import _ from "lodash";
import { DateTime } from "luxon";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { SignupBillingPricePage } from "features/signup/SignupBillingPricePage";
import { MemberContext } from "../MemberContext";
import { getBillingPlansToShow, getInitialPlan } from "./UpdateBillingForm";
import { UpdateLegacyBillingCloseIcon } from "./UpdateLegacyBillingCloseIcon";

const useStyles = makeStyles((theme) => ({
    root: {
        padding: theme.spacing(4),
        marginTop: theme.spacing(4),
        [theme.breakpoints.up("md")]: {
            display: "flex",
        },
    },
    videoColumn: {
        width: "40%",
        [theme.breakpoints.down("md")]: {
            position: "relative",
            width: "100%",
        },
    },
    contentWrapper: {
        width: "60%",
        marginLeft: "40%",
        paddingRight: theme.spacing(15),
        [theme.breakpoints.down("md")]: {
            minHeight: "unset",
            marginLeft: "0",
            width: "100%",
            padding: theme.spacing(8),
            paddingBottom: theme.spacing(15),
        },
        [theme.breakpoints.down("sm")]: {
            minHeight: "unset",
            marginLeft: "0",
            width: "100%",
            padding: theme.spacing(8),
            paddingBottom: theme.spacing(20),
        },
    },
}));

const DonationPage = (props: {
    onSubmit: (donation: number) => void;
    billingChoice: MembershipBillingChoice;
    selectedPlan: MembershipBillingPlan;
}) => {
    const { billingChoice, onSubmit, selectedPlan } = props;
    const classes = useStyles();

    const [newDonationAmount, setNewDonationAmount] = useState<number>(0);

    const PRICE_OFFSET: number =
        billingChoice.plan.membershipType === MembershipType.explorer
            ? 0
            : getMentorshipGroupAddOnPrice(billingChoice.plan.membershipType);

    return (
        <>
            <UpdateLegacyBillingCloseIcon />
            <LandingTwoColumnLayout
                continueButtonText={"Sounds good!"}
                classes={{
                    videoColumn: classes.videoColumn,
                    contentWrapper: classes.contentWrapper,
                }}
                onNext={() => {
                    onSubmit(newDonationAmount);
                }}
                content={
                    <>
                        <Typography variant="h4">
                            Would you like to add a recurring monthly donation?
                            {billingChoice.donationAmount !== 0 && (
                                <>
                                    <br />
                                    <Typography variant="caption">
                                        {`You were previously donating $${displayPrice(
                                            billingChoice.donationAmount,
                                        )} a ${billingChoice.plan.billedEvery}.`}
                                    </Typography>
                                </>
                            )}
                        </Typography>
                        <Box
                            sx={{
                                width: "100%",
                                maxWidth: 350,
                                paddingBottom: "10px",
                            }}
                        >
                            <List dense aria-label="donation">
                                {[10, 20, 50, 250, 500].map((price) => {
                                    return (
                                        <ListItemButton
                                            key={`list-button-${price}`}
                                            selected={newDonationAmount === price}
                                            onClick={() => {
                                                setNewDonationAmount(price);
                                            }}
                                        >
                                            <ListItemText
                                                primary={`${displayPrice(price)} per month`}
                                                secondary={
                                                    newDonationAmount === price
                                                        ? `This brings your total contribution to ${displayPrice(
                                                              price +
                                                                  selectedPlan.baseAmount +
                                                                  PRICE_OFFSET,
                                                          )} a month.`
                                                        : null
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })}

                                <ListItemButton
                                    selected={newDonationAmount === 0}
                                    onClick={() => {
                                        setNewDonationAmount(0);
                                    }}
                                >
                                    <ListItemText primary={`Skip (${displayPrice(0)})`} />
                                </ListItemButton>
                            </List>
                        </Box>
                    </>
                }
                videoUrl="https://storage.googleapis.com/cloud-sangha-images/funnel-experiment-videos/Sky.mp4"
                videoId="slidingScale"
            />{" "}
        </>
    );
};

const ImmersionConfirmationPage = (props: {
    billingChoice: MembershipBillingChoice;
    selectedPlan: MembershipBillingPlan;
    onContinue: () => void;
}) => {
    const classes = useStyles();

    const { billingChoice, selectedPlan, onContinue } = props;

    const PRICE_OFFSET: number =
        billingChoice.plan.membershipType === MembershipType.explorer
            ? 0
            : getMentorshipGroupAddOnPrice(billingChoice.plan.membershipType);

    return (
        <>
            <UpdateLegacyBillingCloseIcon />
            <LandingTwoColumnLayout
                continueButtonText={"Sounds good!"}
                classes={{
                    videoColumn: classes.videoColumn,
                    contentWrapper: classes.contentWrapper,
                }}
                onNext={() => {
                    onContinue();
                }}
                content={
                    <>
                        <Typography variant="h4">
                            {`Your mentorship group is an additional $${getMentorshipGroupAddOnPrice(
                                billingChoice.plan.membershipType,
                            )} a month.`}
                        </Typography>
                        <Typography variant="body1">
                            That makes your total membership $
                            {`${selectedPlan.baseAmount + PRICE_OFFSET}`} a month.{" "}
                        </Typography>
                    </>
                }
                videoUrl="https://storage.googleapis.com/cloud-sangha-images/funnel-experiment-videos/Sky.mp4"
                videoId="slidingScale"
            />{" "}
        </>
    );
};

const UpdateFromLegacyBilling = ({ membership }: { membership: Membership }) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const user = useContext(LoggedInUserProfileContext);

    const billingChoice = membership.scheduledChange?.billingChoice || membership.billingChoice;
    const currentPlanName = billingChoice?.plan.planName;

    const PRICE_OFFSET =
        billingChoice.plan.membershipType === MembershipType.explorer
            ? 0
            : getMentorshipGroupAddOnPrice(billingChoice.plan.membershipType);

    const billingPlans = getBillingPlansToShow(membership, currentPlanName);

    function getInitialPlanValue() {
        const basePlans = user.features.includes(ProductFeature.DEDICATED_SANGHA)
            ? billingPlans.map((plan) => ({ ...plan, baseAmount: plan.baseAmount - PRICE_OFFSET }))
            : billingPlans;
        return getInitialPlan(basePlans, currentPlanName);
    }

    const [selectedPlan, setSelectedPlan] = useState(getInitialPlanValue());
    const [showSuccessPage, setShowSuccessPage] = useState(false);
    const [showDonationPage, setShowDonationPage] = useState(false);
    const [donationAmount] = useState(
        membership.billingChoice ? membership.billingChoice.donationAmount : 10,
    );
    const [redirectAfterBillingChange, setRedirectAfterBillingChange] = useLocalStorage<
        string | undefined
    >("REDIRECT_AFTER_BILLING_PLAN_CHANGE");

    const [updateMembershipMutation, { loading: isSaving, error: updateMembershipError }] =
        useMutation(GRAPHQL_MUTATION_MEMBERSHIP_BILLING, {
            refetchQueries: ["Membership", "MyMemberships"],
        });

    async function handleSubmit(overrideDonationAmount = donationAmount) {
        await updateMembershipMutation({
            variables: {
                membershipId: membership.id,
                billingChoice: {
                    planName: selectedPlan.planName,
                    donationAmount: overrideDonationAmount,
                },
                changeDate: DateTime.now().toISO(),
            },
        });
        postSubmissionActions();
    }

    function postSubmissionActions() {
        showSnackbarAlertOnRedirect("Your contribution has been updated");
        if (redirectAfterBillingChange) {
            dispatch(push(redirectAfterBillingChange));
            setRedirectAfterBillingChange(undefined);
        } else {
            dispatch(push(routes.memberMembership()));
        }
    }

    if (isSaving) {
        return <LoadingPage />;
    }

    if (updateMembershipError) {
        return <GenericErrorPage />;
    }

    if (showDonationPage) {
        return (
            <DonationPage
                billingChoice={billingChoice}
                selectedPlan={selectedPlan}
                onSubmit={handleSubmit}
            />
        );
    }

    if (!user.features.includes(ProductFeature.DEDICATED_SANGHA)) {
        return renderExplorerMembersView();
    }

    if (showSuccessPage) {
        return renderSuccessPageView();
    }

    return renderDefaultView();

    function renderExplorerMembersView() {
        return (
            <>
                <UpdateLegacyBillingCloseIcon />
                <SignupBillingPricePage
                    billingPlanOptions={billingPlans}
                    billingPlan={selectedPlan}
                    onChangeBillingPlan={setSelectedPlan}
                    onContinue={() => {
                        setShowDonationPage(true);
                    }}
                    note={
                        <span>
                            You can elect to change this amount at <strong>any time</strong>.
                        </span>
                    }
                    customTitleText="My base contribution will be "
                />
            </>
        );
    }

    function renderSuccessPageView() {
        return (
            <ImmersionConfirmationPage
                billingChoice={billingChoice}
                selectedPlan={selectedPlan}
                onContinue={() => {
                    setShowDonationPage(true);
                }}
            />
        );
    }

    function renderDefaultView() {
        return (
            <SignupBillingPricePage
                billingPlanOptions={adjustBaseAmountForPlans(billingPlans)}
                billingPlan={selectedPlan}
                onChangeBillingPlan={setSelectedPlan}
                onContinue={() => {
                    setShowSuccessPage(true);
                }}
                note={
                    <span>
                        You can elect to change this amount at <strong>any time</strong>.
                    </span>
                }
                customTitleText="My base contribution will be "
            />
        );
    }

    function adjustBaseAmountForPlans(plans: MembershipBillingPlan[]) {
        return plans.map((plan) => ({ ...plan, baseAmount: plan.baseAmount - PRICE_OFFSET }));
    }
};

export const UpdateLegacyBillingPage3 = () => {
    const context = useContext(MemberContext);
    const membership = _.first(context.memberships);

    if (!membership) {
        return <NotFoundPage />;
    }

    return <UpdateFromLegacyBilling membership={membership} />;
};
